import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import axios from "axios";
import { API, formatApiErrorDetail } from "../App";
import { toast } from "sonner";
import { Users, TrendingUp, KeyRound, Trash2, NotebookPen } from "lucide-react";
import { TradeTable } from "../components/TradeTable";
import { JournalChart } from "../components/JournalChart";

const studentName = (s) => `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email;

export default function AnalisiStudenti() {
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [books, setBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const fetchStudents = () =>
    axios.get(`${API}/journal/admin/users`)
      .then((res) => setStudents(res.data))
      .catch(() => toast.error("Errore nel caricamento degli studenti"))
      .finally(() => setLoading(false));

  useEffect(() => { fetchStudents(); }, []);

  const selectStudent = async (uid) => {
    setSelectedId(uid);
    setData(null);
    setBooks([]);
    setSelectedBookId("");
    setShowReset(false);
    if (!uid) return;
    try {
      const res = await axios.get(`${API}/journal/admin/users/${uid}/books`);
      setBooks(res.data.books);
      if (res.data.books.length > 0) selectBook(uid, res.data.books[0].id);
    } catch {
      toast.error("Errore nel caricamento dei journal");
    }
  };

  const selectBook = async (uid, bid) => {
    setSelectedBookId(bid);
    setData(null);
    if (!bid) return;
    setLoadingTrades(true);
    try {
      const res = await axios.get(`${API}/journal/admin/users/${uid}/trades?journal_id=${bid}`);
      setData(res.data);
    } catch {
      toast.error("Errore nel caricamento dei trade");
    } finally {
      setLoadingTrades(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    try {
      await axios.put(`${API}/journal/admin/users/${selectedId}/password`, { password: newPassword });
      toast.success("Password resettata con successo");
      setNewPassword("");
      setShowReset(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteAccount = async () => {
    const s = students.find((x) => x.id === selectedId);
    if (!window.confirm(`Eliminare DEFINITIVAMENTE l'account di ${studentName(s)} con tutti i suoi journal e trade?`)) return;
    if (!window.confirm("Sei sicuro? L'operazione non è reversibile.")) return;
    try {
      await axios.delete(`${API}/journal/admin/users/${selectedId}`);
      toast.success("Account eliminato");
      setSelectedId("");
      setBooks([]);
      setSelectedBookId("");
      setData(null);
      fetchStudents();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const selectedStudent = students.find((s) => s.id === selectedId);
  const trades = data?.trades || [];
  const wins = trades.filter((t) => t.outcome === "win").length;
  const losses = trades.filter((t) => t.outcome === "loss").length;
  const winRate = wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : null;
  const totalPct = trades.reduce((s, t) => s + (parseFloat(t.percentage) || 0), 0);

  return (
    <div className="page-container" data-testid="analisi-studenti-page">
      <PageHero image={HERO_IMAGES.strumenti} kicker="Admin" title="Analisi Studenti" subtitle="Monitora i progressi e i trade dei tuoi studenti" />
      <SectionBg type="crypto" />
      <div className="container py-8 relative">
        <div className="max-w-6xl mx-auto">
          <div className="glass-card p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 flex items-center justify-center" style={{ backgroundColor: "rgba(47,216,168,0.12)" }}>
                <Users size={20} style={{ color: "var(--mm-gold)" }} />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>Seleziona uno studente</h2>
                <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>{students.length} profili Journal registrati</p>
              </div>
            </div>
            {loading ? (
              <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
            ) : students.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--mm-text-muted)" }} data-testid="no-students-msg">Nessuno studente ha ancora creato un profilo Journal.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Studente</label>
                  <select value={selectedId} onChange={(e) => selectStudent(e.target.value)} className="admin-input" data-testid="student-select">
                    <option value="">— Scegli uno studente —</option>
                    {students.map((s) => {
                      const name = `${s.first_name || ""} ${s.last_name || ""}`.trim();
                      return <option key={s.id} value={s.id}>{name ? `${name} — ${s.email}` : s.email} ({s.trade_count} trade)</option>;
                    })}
                  </select>
                </div>
                {selectedId && books.length > 0 && (
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Journal</label>
                    <select value={selectedBookId} onChange={(e) => selectBook(selectedId, e.target.value)} className="admin-input" data-testid="book-select">
                      {books.map((b) => (
                        <option key={b.id} value={b.id}>{b.name} ({b.trade_count} trade)</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {selectedStudent && (
              <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--mm-border)" }}>
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={() => setShowReset(!showReset)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/5"
                    style={{ border: "1px solid var(--mm-gold)", color: "var(--mm-gold)" }} data-testid="reset-password-btn">
                    <KeyRound size={14} /> Reset Password
                  </button>
                  <button onClick={deleteAccount}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors hover:bg-red-500/10"
                    style={{ border: "1px solid rgba(239,68,68,0.5)", color: "#EF4444" }} data-testid="delete-account-btn">
                    <Trash2 size={14} /> Elimina Account
                  </button>
                </div>
                {showReset && (
                  <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="reset-password-form">
                    <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Nuova password (min 6 caratteri)" className="admin-input" style={{ maxWidth: "280px" }} data-testid="new-password-input" />
                    <button onClick={resetPassword} className="btn-primary text-sm py-2" data-testid="confirm-reset-btn">Conferma Reset</button>
                    <button onClick={() => { setShowReset(false); setNewPassword(""); }} className="btn-secondary text-sm py-2">Annulla</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {loadingTrades && (
            <div className="flex justify-center py-10">
              <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
            </div>
          )}

          {data && (
            <div data-testid="student-trades-section">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp size={20} style={{ color: "var(--mm-gold)" }} />
                <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>
                  <span className="gold-text">{studentName(data.user)}</span>
                  <span className="mx-2" style={{ color: "var(--mm-text-muted)" }}>·</span>
                  <span className="inline-flex items-center gap-1.5"><NotebookPen size={15} style={{ color: "var(--mm-text-muted)" }} />{books.find(b => b.id === selectedBookId)?.name}</span>
                </h3>
              </div>
              {trades.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
                  {[
                    { label: "Trade Totali", value: trades.length, color: "var(--mm-gold)" },
                    { label: "Win", value: wins, color: "#2FD8A8" },
                    { label: "Loss", value: losses, color: "#EF4444" },
                    { label: "Win Rate", value: winRate != null ? `${winRate}%` : "—", color: "#64C8FF" },
                    { label: "% Cumulata", value: `${totalPct.toFixed(2)}%`, color: totalPct >= 0 ? "#2FD8A8" : "#EF4444" },
                  ].map((s, i) => (
                    <div key={i} className="glass-card p-5 text-center" data-testid={`admin-stat-${i}`}>
                      <p className="text-xl sm:text-2xl font-bold" style={{ color: s.color, fontFamily: "'Space Grotesk', sans-serif" }}>{s.value}</p>
                      <p className="text-xs uppercase mt-1" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <JournalChart trades={trades} />
              <TradeTable trades={trades} readOnly />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
